import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Languages, Home, BookOpen, FileText, Layers, GitBranch, 
  ChevronLeft, ChevronDown, ChevronRight, User, LogOut, Loader2, 
  Settings, FileEdit, Volume2, Shield, PenTool, Clock
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';

import { API_BASE } from '../config/api';
const API = `${API_BASE}`;

// Hierarchical navigation structure
const getNavStructure = (partsOfSpeech = [], writingSystems = []) => {
  // Build dynamic inflection children based on POS with inflection
  const inflectablePOS = partsOfSpeech.filter(pos => pos.has_inflection);
  const inflectionChildren = [
    { id: 'inflection-all', label: 'All Rules', path: '/inflection' },
    ...inflectablePOS.map(pos => ({
      id: `inflection-${pos.name.toLowerCase()}`,
      label: `${pos.name}s`,
      path: `/inflection/${pos.name.toLowerCase()}`,
    })),
  ];

  // Build dynamic writing system children
  const writingChildren = writingSystems.map(ws => ({
    id: `ws-${ws.system_id}`,
    label: ws.name,
    path: `/writing/${ws.system_id}`,
  }));

  return [
    { 
      id: 'overview', 
      label: 'Overview', 
      icon: Home, 
      path: '' 
    },
    // Writing section: dynamic children if systems exist, otherwise simple link
    ...(writingChildren.length > 0
      ? [{
          id: 'writing',
          label: 'Writing',
          icon: PenTool,
          children: [
            { id: 'writing-all', label: 'All Systems', path: '/writing' },
            ...writingChildren,
          ]
        }]
      : [{
          id: 'writing',
          label: 'Writing',
          icon: PenTool,
          path: '/writing'
        }]
    ),
    { 
      id: 'phonology', 
      label: 'Phonology', 
      icon: Volume2,
      children: [
        { id: 'phonemes', label: 'Phonemes', path: '/phonology' },
        { id: 'phonotactics', label: 'Phonotactics', path: '/phonotactics' },
        { id: 'allophones', label: 'Allophones', path: '/allophones' },
        { id: 'prosody', label: 'Prosody', path: '/prosody' },
      ]
    },
    { 
      id: 'syntax', 
      label: 'Syntax', 
      icon: GitBranch,
      children: [
        { id: 'word-order', label: 'Word Order', path: '/word-order' },
        { id: 'parts-of-speech', label: 'Parts of Speech', path: '/parts-of-speech' },
        { id: 'grammar', label: 'Grammar', path: '/grammar' },
      ]
    },
    { 
      id: 'morphology', 
      label: 'Morphology', 
      icon: Layers,
      children: [
        { id: 'morphology-desc', label: 'Description', path: '/morphology-description' },
        { id: 'derivation', label: 'Derivation', path: '/derivation' },
        ...(inflectionChildren.length > 1
          ? [{ id: 'inflection-group', label: 'Inflection', isGroup: true, children: inflectionChildren }]
          : [{ id: 'inflection', label: 'Inflection', path: '/inflection' }]),
      ]
    },
    { 
      id: 'lexicon', 
      label: 'Lexicon', 
      icon: BookOpen,
      children: [
        { id: 'pronouns', label: 'Pronouns', path: '/lexicon/pronouns' },
        { id: 'numbers', label: 'Numbers', path: '/lexicon/numbers' },
        { id: 'kinship', label: 'Kinship', path: '/lexicon/kinship' },
        { id: 'colors', label: 'Color Terms', path: '/lexicon/colors' },
        { id: 'body-parts', label: 'Body Parts', path: '/lexicon/body-parts' },
        { id: 'dictionary', label: 'Dictionary', path: '/lexicon' },
      ]
    },
    { 
      id: 'texts', 
      label: 'Texts', 
      icon: FileEdit, 
      path: '/texts' 
    },
    { 
      id: 'activity', 
      label: 'Activity Log', 
      icon: Clock, 
      path: '/activity' 
    },
  ];
};

const ConlangLayout = () => {
  const { conlangId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [conlang, setConlang] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState([]);
  const [partsOfSpeech, setPartsOfSpeech] = useState([]);
  const [writingSystems, setWritingSystems] = useState([]);

  useEffect(() => {
    fetchConlang();
    fetchPartsOfSpeech();
    fetchWritingSystems();
  }, [conlangId]);

  // Auto-expand only the section containing the active page (accordion)
  useEffect(() => {
    const currentPath = location.pathname.replace(`/conlang/${conlangId}`, '');
    const navStructure = getNavStructure(partsOfSpeech, writingSystems);
    let activeSection = null;
    navStructure.forEach(item => {
      if (item.children) {
        const isChildActive = item.children.some(child => {
          if (child.isGroup && child.children) {
            return child.children.some(gc => currentPath === gc.path || currentPath.startsWith(gc.path + '/'));
          }
          return currentPath === child.path || currentPath.startsWith(child.path + '/');
        });
        if (isChildActive) activeSection = item.id;
      }
    });
    if (activeSection) {
      setExpandedSections([activeSection]);
    }
  }, [location.pathname, conlangId, partsOfSpeech, writingSystems]);

  const fetchPartsOfSpeech = async () => {
    try {
      const response = await axios.get(`${API}/conlangs/${conlangId}/parts-of-speech`, { withCredentials: true });
      setPartsOfSpeech(response.data || []);
    } catch { /* silent */ }
  };

  const fetchWritingSystems = async () => {
    try {
      const response = await axios.get(`${API}/conlangs/${conlangId}/writing-systems`, { withCredentials: true });
      setWritingSystems(response.data || []);
    } catch { /* silent */ }
  };

  const fetchConlang = async () => {
    try {
      const response = await axios.get(`${API}/conlangs/${conlangId}`, {
        withCredentials: true
      });
      setConlang(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Language not found');
        navigate('/dashboard');
      } else if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load language');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) ? [] : [sectionId]
    );
  };

  const isActive = (path) => {
    const currentPath = location.pathname.replace(`/conlang/${conlangId}`, '');
    if (path === '') return currentPath === '' || currentPath === '/';
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  const isSectionActive = (section) => {
    if (section.path !== undefined) return isActive(section.path);
    if (section.children) {
      return section.children.some(child => {
        if (child.isGroup && child.children) {
          return child.children.some(gc => isActive(gc.path));
        }
        return isActive(child.path);
      });
    }
    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  if (!conlang) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Full-width Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <div className="w-10 h-10 rounded-md bg-primary/20 flex items-center justify-center">
              <Languages className="w-5 h-5 text-primary" />
            </div>
            <span className="font-heading text-xl font-semibold text-foreground">
              <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
            </span>
          </Link>

          <div className="flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <User className="w-4 h-4 text-primary" />
                  )}
                </div>
                <span className="text-foreground-muted hidden sm:block">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-background-paper border-border">
              <DropdownMenuItem 
                onClick={() => navigate('/profile')}
                className="text-foreground-muted hover:text-foreground cursor-pointer"
              >
                <User className="w-4 h-4 mr-2" />
                Profile Settings
              </DropdownMenuItem>
              {user?.is_admin && (
                <DropdownMenuItem 
                  onClick={() => navigate('/admin')}
                  className="text-primary hover:text-primary cursor-pointer"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin Panel
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-foreground-muted hover:text-foreground cursor-pointer"
                data-testid="logout-menu-item"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Content area with sidebar */}
      <div className="flex flex-1">
        {/* Sidebar - now below header */}
        <aside className="w-60 bg-background-paper border-r border-border flex flex-col flex-shrink-0">
          {/* Language info */}
          <div className="p-4 border-b border-border">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 text-foreground-muted hover:text-foreground transition-colors mb-3"
              data-testid="back-to-dashboard"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">All Languages</span>
            </Link>
            <div>
              <h1 className="font-heading text-lg font-semibold text-foreground truncate" data-testid="conlang-name">
                {conlang.name}
              </h1>
              {conlang.native_name && (
                <p className="font-conlang text-sm text-secondary truncate" data-testid="conlang-native-name">
                  {conlang.native_name}
                </p>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {getNavStructure(partsOfSpeech, writingSystems).map((item) => {
              const Icon = item.icon;
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedSections.includes(item.id);
              const sectionActive = isSectionActive(item);

              if (hasChildren) {
                return (
                  <div key={item.id}>
                    {/* Parent item */}
                    <button
                      onClick={() => toggleSection(item.id)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md transition-colors ${
                        sectionActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground-muted hover:text-foreground hover:bg-accent'
                      }`}
                      data-testid={`nav-${item.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4" />
                        <span className="font-medium text-sm">{item.label}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Children */}
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-1 border-l border-border pl-3">
                        {item.children.map((child) => {
                          if (child.isGroup && child.children) {
                            // Render a nested group (e.g., Inflection with POS sub-items)
                            const groupExpanded = expandedSections.includes(child.id);
                            const groupActive = child.children.some(gc => isActive(gc.path));
                            return (
                              <div key={child.id}>
                                <button
                                  onClick={() => toggleSection(child.id)}
                                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                                    groupActive
                                      ? 'text-primary font-medium'
                                      : 'text-foreground-muted hover:text-foreground hover:bg-accent'
                                  }`}
                                  data-testid={`nav-${child.id}`}
                                >
                                  <span>{child.label}</span>
                                  {groupExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                </button>
                                {groupExpanded && (
                                  <div className="ml-3 mt-1 space-y-1 border-l border-border/50 pl-3">
                                    {child.children.map(gc => (
                                      <Link
                                        key={gc.id}
                                        to={`/conlang/${conlangId}${gc.path}`}
                                        className={`block px-3 py-1.5 rounded-md text-xs transition-colors ${
                                          isActive(gc.path)
                                            ? 'bg-primary/20 text-primary font-medium'
                                            : 'text-foreground-muted hover:text-foreground hover:bg-accent'
                                        }`}
                                        data-testid={`nav-${gc.id}`}
                                      >
                                        {gc.label}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          const childActive = isActive(child.path);
                          return (
                            <Link
                              key={child.id}
                              to={`/conlang/${conlangId}${child.path}`}
                              className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                                childActive
                                  ? 'bg-primary/20 text-primary font-medium'
                                  : 'text-foreground-muted hover:text-foreground hover:bg-accent'
                              }`}
                              data-testid={`nav-${child.id}`}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              // Simple item without children
              return (
                <Link
                  key={item.id}
                  to={`/conlang/${conlangId}${item.path}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary/20 text-primary'
                      : 'text-foreground-muted hover:text-foreground hover:bg-accent'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium text-sm">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-3 border-t border-border">
            <Link
              to={`/conlang/${conlangId}/settings`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                isActive('/settings')
                  ? 'bg-primary/20 text-primary'
                  : 'text-foreground-muted hover:text-foreground hover:bg-accent'
              }`}
              data-testid="nav-settings"
            >
              <Settings className="w-4 h-4" />
              <span className="font-medium text-sm">Settings</span>
            </Link>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet context={{ conlang, setConlang, fetchConlang, fetchWritingSystems, canEdit: !localStorage.getItem('impersonating') && (conlang?.access_level === 'owner' || conlang?.access_level === 'edit') }} />
        </main>
      </div>
    </div>
  );
};

export default ConlangLayout;
